import { useEffect, useState } from "react";
import { Award, Briefcase, ContactRound, CalendarCheck } from "lucide-react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import type { Job, User } from "../types/job";
import JobsTable from "../Components/JobTable";
import { API_ENDPOINTS } from "../config/api";
import apiClient from "../services/apiClient";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const showApiError = (error: any) => {
    const message = error?.response?.data?.detail || error?.message || "Action not allowed";
    alert(message);
};
function UserDashboard() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<Job[]>([]);
    const [user, setUser] = useState<User | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const token = localStorage.getItem("token");
    const [editingJob, setEditingJob] = useState<Job | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const isAdmin = user?.role?.role_id === 1 || user?.role?.role_id === 2;
    console.log("isAdmin", isAdmin);
    console.log("user", user);

    const [dashboardStats, setDashboardStats] = useState({
        total_jobs: 0,
        total_functions: 0,
        active_jobs: 0,
        total_users: 0,
        active_users: 0,
    });

    const [form, setForm] = useState<{
        job_function: string;
        job_title: string;
        job_description: string;
        job_mandatory_skills: string;
        job_optional_skills: string;
        job_primary_language: string;
    }>({
        job_function: "",
        job_title: "",
        job_description: "",
        job_mandatory_skills: "",
        job_optional_skills: "",
        job_primary_language: "",
    });

    useEffect(() => {
        const token = localStorage.getItem("token");

        const fetchData = async () => {
            try {
                const userRes = await apiClient.get<User>(API_ENDPOINTS.ME);
                setUser(userRes.data);

                const jobRes = await apiClient.get<Job[]>(API_ENDPOINTS.JOBS);

                const tenantJobs = jobRes.data.filter((job) => job.tenant_id === userRes.data.tenant_id);
                setJobs(tenantJobs);

                await fetchDashboardStats();
            } catch (err) {
                console.error("Error fetching dashboard data", err);
            }
        };

        fetchData();
    }, []);


    const openModal = (job: Job | null = null) => {
        setEditingJob(job);
        setForm(
            job
                ? {
                    job_function: job.job_function || "",
                    job_title: job.job_title || "",
                    job_description: job.job_description || "",
                    job_mandatory_skills: job.job_mandatory_skills || "",
                    job_optional_skills: job.job_optional_skills || "",
                    job_primary_language: job.job_primary_language || "",
                }
                : {
                    job_function: "",
                    job_title: "",
                    job_description: "",
                    job_mandatory_skills: "",
                    job_optional_skills: "",
                    job_primary_language: "",
                }
        );
        setIsModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!user) return;
        setIsSaving(true);

        const jobData = {
            ...form,
            tenant_id: user.tenant_id,
            created_by: user.user_id,
            job_status: "active",
        };
        try {
            if (editingJob) {
                const res = await apiClient.put<Job>(API_ENDPOINTS.JOB_BY_ID(editingJob.job_id.toString()), jobData);
                setJobs(jobs.map(j => (j.job_id === editingJob.job_id ? res.data : j)));
            }
            else {
                const res = await apiClient.post<Job>(API_ENDPOINTS.JOBS, jobData);
                setJobs([...jobs, res.data]);
            }
            await fetchDashboardStats();
            setIsModalOpen(false);
        } catch (error) {
            showApiError(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (job_id: number) => {
        if (!token) return;
        try {
            await apiClient.delete(API_ENDPOINTS.JOB_BY_ID(job_id.toString()));
            setJobs(jobs.filter(j => j.job_id !== job_id));
            await fetchDashboardStats();
        } catch (error) {
            showApiError(error);
        }
    };

    const stats = [
        { label: t("dashboardPage.stats.totalFunctions"), value: dashboardStats.total_functions.toString(), change: '+0%', icon: Award },
        { label: t("dashboardPage.stats.totalJobs"), value: dashboardStats.total_jobs.toString(), change: '+0%', icon: Briefcase },
        { label: t("dashboardPage.stats.totalActiveJobs"), value: dashboardStats.active_jobs.toString(), change: '+0%', icon: CalendarCheck },
        { label: t("dashboardPage.stats.totalUsers"), value: dashboardStats.total_users.toString(), change: '+0%', icon: ContactRound, onClick: isAdmin ? () => navigate("/profile") : undefined },
    ];

    const handleStatusToggle = async (job: Job) => {
        if (!token) return;

        const newStatus = job.job_status === "active" ? "inactive" : "active";

        try {
            const res = await apiClient.patch<Job>(API_ENDPOINTS.JOB_STATUS(job.job_id.toString()), { job_status: newStatus });

            setJobs(prev =>
                prev.map(j =>
                    j.job_id === job.job_id ? { ...j, job_status: res.data.job_status } : j
                )
            );
            await fetchDashboardStats();
        } catch (error) {
            showApiError(error);
        }
    };
    const fetchDashboardStats = async () => {
        if (!token) return;

        const statsRes = await apiClient.get<{
            total_jobs: number;
            total_functions: number;
            active_jobs: number;
            total_users: number;
            active_users: number;
        }>(API_ENDPOINTS.JOB_STATS);

        setDashboardStats(statsRes.data);
    };
    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            <div className="max-w-[83rem] mx-auto px-4 sm:px-6 lg:px-[0rem] py-8">
                <div className="bg-white border border-gray-200 rounded-2xl p-8 text-gray-900 shadow-sm mb-8">
                    <div className="grid grid-cols-1 xl:grid-cols-7 gap-6 items-start">

                        <div className="xl:col-span-3">
                            <h2 className="text-2xl text-primary font-bold mb-1 flex flex-wrap gap-[1px]">
                                {t("dashboardPage.welcome")}, {user?.name} !
                            </h2>

                            <p className="text-gray-700 text-sm">
                                {t("dashboardPage.subtitle")}
                            </p>
                        </div>

                        {stats.map((stat, index) => {
                            const Icon = stat.icon;
                            const isClickable = Boolean(stat.onClick);

                            return (
                                <div
                                    key={index}
                                    onClick={isClickable ? stat.onClick : undefined}
                                    className={`
                                    bg-gray-50 rounded-lg p-3 min-h-[80px] shadow-sm border border-gray-200
                                    transition-all flex items-center gap-3 xl:col-span-1
                                    ${isClickable ? "cursor-pointer hover:shadow-md hover:bg-gray-100" : ""}
                                `}
                                >
                                    <div className="w-8 h-8 bg-secondary rounded-md flex items-center justify-center">
                                        <Icon className="w-4 h-4 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-gray-900">{stat.value}</h3>
                                        <p className="text-gray-500 text-xs leading-none">{stat.label}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="mb-8 bg-white rounded-xl shadow-sm p-6 border border-gray-200 ">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">{t("dashboardPage.jobs.manageJobs")}</h3>
                        {isAdmin && (
                            <button
                                onClick={() => openModal()}
                                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-800 hover:text-secondary transition-colors shadow-sm"
                            >
                                {t("dashboardPage.jobs.addJob")}
                            </button>
                        )}
                    </div>
                    <JobsTable jobs={jobs} openModal={openModal} handleDelete={handleDelete} handleStatusToggle={handleStatusToggle} isAdmin={isAdmin} />
                </div>

                {isModalOpen && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="relative bg-white border border-gray-200 rounded-xl shadow-xl w-full max-w-3xl p-6 text-gray-900 max-h-[95vh] overflow-y-auto">
                            <h3 className="text-lg font-semibold mb-4">
                                {editingJob ? t("dashboardPage.jobs.editJob") : t("dashboardPage.jobs.createJob")}
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t("dashboardPage.form.jobFunction")}
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={t("dashboardPage.placeholders.jobFunction")}
                                        value={form.job_function}
                                        onChange={(e) => setForm({ ...form, job_function: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t("dashboardPage.form.jobTitle")}
                                    </label>
                                    
                                    <input
                                        type="text"
                                        placeholder={t("dashboardPage.placeholders.jobTitle")}
                                        value={form.job_title}
                                        onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                    />
                                </div>
                                <div className="space-y-1 col-span-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t("dashboardPage.form.jobDescription")}
                                    </label>
                                    <textarea
                                        placeholder={t("dashboardPage.placeholders.jobDescription")}
                                        value={form.job_description}
                                        onChange={(e) => setForm({ ...form, job_description: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent outline-none h-24"
                                    />
                                </div>
                                <div className="space-y-1 col-span-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t("dashboardPage.form.mandatorySkills")}
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={t("dashboardPage.placeholders.mandatorySkills")}
                                        value={form.job_mandatory_skills}
                                        onChange={(e) => setForm({ ...form, job_mandatory_skills: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                    />
                                </div>
                                <div className="space-y-1 col-span-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t("dashboardPage.form.optionalSkills")}
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={t("dashboardPage.placeholders.optionalSkills")}
                                        value={form.job_optional_skills}
                                        onChange={(e) => setForm({ ...form, job_optional_skills: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                    />
                                </div>
                                <div className="space-y-1 col-span-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t("dashboardPage.form.primaryLanguage")}
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={t("dashboardPage.placeholders.primaryLanguage")}
                                        value={form.job_primary_language}
                                        onChange={(e) => setForm({ ...form, job_primary_language: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end mt-6 space-x-2">
                                <button
                                    onClick={() => !isSaving && setIsModalOpen(false)}
                                    disabled={isSaving}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {t("common.cancel")}
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={isSaving}
                                    className={`px-4 py-2 rounded-lg transition-colors text-white
                                    ${isSaving ? "bg-gray-400 cursor-not-allowed" : "bg-primary hover:bg-blue-800"}`}
                                >
                                    {isSaving ? t("common.saving") : t("common.save")}
                                </button>
                            </div>
                        </div>
                        {isSaving && (
                            <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-xl z-50">
                                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}

export default UserDashboard;